import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import apiHelpers from '../../utils/apiHelpers';
import Button from '../common/Button';
import Loader from '../common/Loader';
import Alert from '../common/Alert';
import CardsNotFoundList from '../card/CardsNotFoundList';

const MODES = [
  {
    value: 'overwrite',
    label: 'Reemplazar la colección',
    description:
      'Borra toda tu colección actual y la reemplaza por el contenido del archivo. Las cartas que no estén en el CSV se eliminan.',
  },
  {
    value: 'merge',
    label: 'Actualizar la colección',
    description:
      'No borra nada. Para cada carta del CSV reemplaza la cantidad de copias por la del archivo, y agrega las cartas que todavía no tengas. Las cartas que no estén en el CSV se mantienen sin cambios.',
  },
];

const ImportCollectionModal = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('merge');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState(null);

  const close = () => {
    setFile(null);
    setErrorMessage('');
    setResult(null);
    onClose();
  };

  const handleImport = () => {
    if (!file) {
      setErrorMessage('Elegí un archivo CSV.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    setResult(null);
    apiHelpers
      .importCollection(file, mode)
      .then(response => {
        setResult(response.data);
        setLoading(false);
        onImported();
      })
      .catch(error => {
        setLoading(false);
        setErrorMessage(
          error.response?.data?.message ||
            'No se pudo importar el archivo. Revisá que sea un CSV de colección de Moxfield.',
        );
        setResult(
          error.response?.data?.cardsNotFound
            ? { cardsNotFound: error.response.data.cardsNotFound }
            : null,
        );
      });
  };

  return (
    <Dialog open={open} onClose={close} className="relative z-50">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
      <div className="fixed inset-0 flex items-start justify-center p-4 overflow-y-auto">
        <DialogPanel className="w-full max-w-xl bg-darkColor text-white rounded-md p-6 flex flex-col gap-y-4 mt-16">
          <DialogTitle className="text-xl">
            <i className="fas fa-file-csv mr-2"></i>
            Importar colección desde Moxfield
          </DialogTitle>

          <p className="text-sm text-gray-300">
            Exportá tu colección de Moxfield como CSV y subí el archivo acá. Se
            leen las columnas <strong>Count</strong> y <strong>Name</strong>: si
            tenés la misma carta en varias ediciones, las copias se suman.
          </p>

          <input
            type="file"
            accept=".csv,text/csv"
            className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-accentDark file:text-white hover:file:bg-accentLight"
            onChange={event => setFile(event.target.files[0] || null)}
          />

          <div className="flex flex-col gap-y-3">
            {MODES.map(option => (
              <label
                key={option.value}
                className="flex flex-row items-start gap-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="importMode"
                  className="mt-1 shrink-0"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                />
                <span className="flex flex-col">
                  <span className="font-semibold">{option.label}</span>
                  <span className="text-sm text-gray-300">
                    {option.description}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {loading && <Loader />}
          <Alert>{errorMessage}</Alert>

          {result &&
            (result.imported !== undefined || result.updated !== undefined) && (
              <p className="text-sm">
                Listo: {result.imported} agregadas, {result.updated}{' '}
                actualizadas
                {result.deleted ? `, ${result.deleted} eliminadas` : ''}.
              </p>
            )}
          {result &&
            result.cardsNotFound &&
            result.cardsNotFound.length > 0 && (
              <CardsNotFoundList cardsNotFound={result.cardsNotFound} />
            )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-upload"
              onClick={handleImport}
              disabled={loading || !file}>
              Importar
            </Button>
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-times"
              onClick={close}
              disabled={loading}>
              Cerrar
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default ImportCollectionModal;
